import React from 'react';
import {
  BuildIcon,
  KindTag,
  LEAD_WIDTH,
  TITLE_FLEX,
  VALUE_WIDTH,
  type LedgerCollapse,
  type LedgerColumn,
  Text,
} from '@billda/design-system';
import type { LabourItemPricingDetails } from '@billda/api';
import { formatCurrency } from '../../../../../shared/utils/currency';
import {
  formatQuantity,
  formatUnitRate,
} from '../../../../../shared/utils/quantity';
import type { LabourItemDiff, ChangeType } from '../../../types/reimportTypes';

interface ReimportLabourItemRowProps {
  item: LabourItemDiff;
}

export const REIMPORT_COLLAPSE: LedgerCollapse = {
  container: 'reimport-comparison',
  at: 760,
};

export const REIMPORT_COLUMNS: LedgerColumn[] = [
  { key: 'current', label: 'Current', flex: 1.4 },
  { key: 'imported', label: 'Imported', flex: 1.4 },
];

/** Tailwind container-query classes matching REIMPORT_COLLAPSE (760px). */
const HIDE_BELOW = '@max-[760px]:hidden';
const SHOW_BELOW_BLOCK = 'hidden @max-[760px]:block';
const SHOW_BELOW_GRID = 'hidden @max-[760px]:grid';

const changeTypeLabel: Record<ChangeType, string | null> = {
  Added: 'Added',
  Removed: 'Removed',
  Modified: null,
  Unchanged: 'No change',
};

const pricingModeLabel = (
  pricing: LabourItemPricingDetails | null | undefined
) => (pricing?.pricingMode === 'UnitRate' ? 'Unit rate' : 'Fixed amount');

const signed = (value: number, formatted: string) =>
  `${value > 0 ? '+' : '−'}${formatted}`;

export const getChangeDetails = (item: LabourItemDiff): string[] => {
  if (item.changeType !== 'Modified') return [];

  const details: string[] = [];
  const current = item.currentPricing;
  const next = item.newPricing;

  if (current?.pricingMode !== next?.pricingMode) {
    details.push(`${pricingModeLabel(current)} → ${pricingModeLabel(next)}`);
  } else if (
    current?.pricingMode === 'UnitRate' &&
    next?.pricingMode === 'UnitRate'
  ) {
    if (
      current.totalQuantity != null &&
      next.totalQuantity != null &&
      current.totalQuantity !== next.totalQuantity
    ) {
      const delta = next.totalQuantity - current.totalQuantity;
      details.push(
        `Quantity ${signed(delta, formatQuantity(Math.abs(delta), next.unitLabel ?? current.unitLabel ?? ''))}`
      );
    }
    if (
      current.unitRate != null &&
      next.unitRate != null &&
      current.unitRate !== next.unitRate
    ) {
      const delta = next.unitRate - current.unitRate;
      details.push(
        `Rate ${signed(delta, formatUnitRate(Math.abs(delta), next.unitLabel ?? current.unitLabel ?? ''))}`
      );
    }
    if (current.unitLabel !== next.unitLabel) {
      details.push(
        `Unit ${current.unitLabel ?? '—'} → ${next.unitLabel ?? '—'}`
      );
    }
  }

  if (
    item.currentPrice != null &&
    item.newPrice != null &&
    item.currentPrice !== item.newPrice
  ) {
    const delta = item.newPrice - item.currentPrice;
    details.push(`Value ${signed(delta, formatCurrency(Math.abs(delta)))}`);
  }

  return details;
};

export const getPriceDelta = (item: LabourItemDiff): number => {
  const current = item.currentPrice ?? 0;
  const imported =
    item.changeType === 'Removed' ? 0 : (item.newPrice ?? current);
  return imported - current;
};

export const SignedPriceDelta: React.FC<{
  value: number;
  skipped?: boolean;
}> = ({ value, skipped = false }) => {
  if (skipped) {
    return (
      <Text variant="caption" className="font-semibold text-content-warning">
        Skipped
      </Text>
    );
  }

  return (
    <Text
      variant="body2"
      noWrap
      className={`font-semibold tabular-nums ${
        value > 0
          ? 'text-content-success'
          : value < 0
            ? 'text-content-error'
            : 'text-content-secondary'
      }`}
    >
      {value === 0
        ? '£0.00'
        : `${value > 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`}
    </Text>
  );
};

interface PricingSnapshotProps {
  label?: string;
  pricing: LabourItemPricingDetails | null | undefined;
  price: number | null;
  emptyLabel: string;
}

const PricingSnapshot: React.FC<PricingSnapshotProps> = ({
  label,
  pricing,
  price,
  emptyLabel,
}) => {
  const isUnitRate = pricing?.pricingMode === 'UnitRate';
  const hasCompleteUnitPricing =
    isUnitRate &&
    pricing.totalQuantity != null &&
    pricing.unitRate != null &&
    !!pricing.unitLabel;

  return (
    <div className="min-w-0">
      {label && (
        <Text variant="caption" color="secondary" className="mb-0.5 block">
          {label}
        </Text>
      )}
      <Text variant="body2" noWrap className="tabular-nums">
        {price == null
          ? emptyLabel
          : hasCompleteUnitPricing
            ? `${formatQuantity(pricing.totalQuantity!, pricing.unitLabel!)} × ${formatUnitRate(pricing.unitRate!, pricing.unitLabel!)}`
            : isUnitRate
              ? 'Incomplete unit pricing'
              : 'Fixed amount'}
      </Text>
      {price != null && (
        <Text
          variant="caption"
          noWrap
          className="block font-semibold tabular-nums"
        >
          {formatCurrency(price)}
        </Text>
      )}
    </div>
  );
};

const ReimportLabourItemRow: React.FC<ReimportLabourItemRowProps> = ({
  item,
}) => {
  const details = getChangeDetails(item).filter(
    detail => !detail.startsWith('Value ')
  );
  const tag = item.isProtected ? 'Skipped' : changeTypeLabel[item.changeType];
  const delta = getPriceDelta(item);

  return (
    <div
      data-testid="reimport-labour-item-row"
      className={`flex min-h-14 items-center gap-3 border-b border-border-default px-4 py-2 ${
        item.isProtected ? 'bg-surface-warning-subtle' : 'bg-surface-default'
      }`}
    >
      <div className="min-w-0" style={{ flex: `${TITLE_FLEX} 1 0` }}>
        <div className="flex min-w-0 items-center gap-2">
          <div
            className="flex shrink-0 justify-center"
            style={{ width: LEAD_WIDTH }}
          >
            <BuildIcon
              className="size-4 text-content-secondary"
              aria-hidden="true"
            />
          </div>
          <Text variant="body2" noWrap className="font-medium">
            {item.definitionName}
          </Text>
          {tag && (
            <KindTag color={item.isProtected ? 'warning.main' : undefined}>
              {tag}
            </KindTag>
          )}
          <div className={`ml-auto ${SHOW_BELOW_BLOCK}`}>
            <SignedPriceDelta value={delta} skipped={item.isProtected} />
          </div>
        </div>

        {details.length > 0 && (
          <Text
            variant="caption"
            color="secondary"
            noWrap
            className="block"
            style={{ paddingLeft: `${LEAD_WIDTH + 8}px` }}
          >
            {details.join(' · ')}
          </Text>
        )}
        {item.isProtected && (
          <Text
            variant="caption"
            className="block whitespace-nowrap text-content-warning"
            noWrap
            style={{ paddingLeft: `${LEAD_WIDTH + 8}px` }}
          >
            {item.activeAllocationCount} active allocation
            {item.activeAllocationCount === 1 ? '' : 's'} ·{' '}
            {formatCurrency(item.totalValueBooked)} booked
          </Text>
        )}

        <div
          className={`mt-2 grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 ${SHOW_BELOW_GRID}`}
          style={{ paddingLeft: `${LEAD_WIDTH + 8}px` }}
        >
          <PricingSnapshot
            label="Current"
            pricing={item.currentPricing}
            price={item.currentPrice}
            emptyLabel="Not on site"
          />
          <PricingSnapshot
            label="Imported"
            pricing={item.newPricing}
            price={item.newPrice}
            emptyLabel={
              item.changeType === 'Removed' ? 'Removed' : 'Not on site'
            }
          />
        </div>
      </div>

      <div className={`min-w-0 ${HIDE_BELOW}`} style={{ flex: '1.4 1 0' }}>
        <PricingSnapshot
          pricing={item.currentPricing}
          price={item.currentPrice}
          emptyLabel="Not on site"
        />
      </div>
      <div className={`min-w-0 ${HIDE_BELOW}`} style={{ flex: '1.4 1 0' }}>
        <PricingSnapshot
          pricing={item.newPricing}
          price={item.newPrice}
          emptyLabel={item.changeType === 'Removed' ? 'Removed' : 'Not on site'}
        />
      </div>
      <div
        className={`shrink-0 text-right ${HIDE_BELOW}`}
        style={{ width: VALUE_WIDTH }}
      >
        <SignedPriceDelta value={delta} skipped={item.isProtected} />
      </div>
    </div>
  );
};

export default ReimportLabourItemRow;
